import { Link } from "react-router-dom";
import { ArrowRight, Mail, Trash2, User, ShieldAlert } from "lucide-react";
import PageShell from "@/components/site/PageShell";

const steps = [
  "Open the Dishly app on your device.",
  "Tap the Profile tab.",
  "Tap Delete Account.",
  "Confirm your choice to permanently delete your account.",
];

const removed = [
  "Your account and profile",
  "Your order history",
  "Your reviews and ratings",
  "Your saved delivery addresses",
  "Your messages with creators",
  "Any uploaded documents (for creators)",
];

export default function DeleteAccount() {
  return (
    <PageShell title="Delete Account" testId="delete-account-page">
      <div className="mx-auto max-w-3xl px-5 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-widest text-terracotta">Account</p>
        <h1 className="mt-3 text-4xl font-black leading-tight tracking-tight text-charcoal sm:text-5xl">
          Delete your Dishly account
        </h1>
        <p className="mt-4 max-w-2xl text-base text-charcoal/70 sm:text-lg">
          You can permanently delete your Dishly account at any time. Deletion is
          immediate and cannot be undone.
        </p>

        {/* In-app steps */}
        <div className="mt-10 rounded-2xl border border-black/5 bg-white p-6 sm:p-8" data-testid="delete-inapp-block">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta/10 text-terracotta">
              <User className="h-5 w-5" />
            </span>
            <h2 className="text-xl font-bold text-charcoal">Delete in the app</h2>
          </div>
          <ol className="mt-5 space-y-3">
            {steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-charcoal text-[12px] font-bold text-cream">
                  {i + 1}
                </span>
                <span className="text-[15px] leading-relaxed text-charcoal/80">{s}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Email path */}
        <div className="mt-6 rounded-2xl border border-black/5 bg-white p-6 sm:p-8" data-testid="delete-email-block">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta/10 text-terracotta">
              <Mail className="h-5 w-5" />
            </span>
            <h2 className="text-xl font-bold text-charcoal">Or request deletion by email</h2>
          </div>
          <p className="mt-3 text-[15px] leading-relaxed text-charcoal/70">
            If you can't access the app, email us from the address on your Dishly account and we'll
            process the deletion for you.
          </p>
          <a
            href="mailto:support@dinewithdishly.co?subject=Delete%20my%20Dishly%20account"
            data-testid="delete-email-btn"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-3 text-[14px] font-bold text-white transition-transform hover:scale-[1.02]"
          >
            Email support@dinewithdishly.co <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        {/* What gets removed */}
        <div className="mt-6 rounded-2xl border border-black/5 bg-white p-6 sm:p-8" data-testid="delete-what-removed">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta/10 text-terracotta">
              <Trash2 className="h-5 w-5" />
            </span>
            <h2 className="text-xl font-bold text-charcoal">What gets permanently removed</h2>
          </div>
          <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {removed.map((r) => (
              <li key={r} className="flex items-center gap-2 text-[15px] text-charcoal/80">
                <span className="h-1.5 w-1.5 rounded-full bg-terracotta" /> {r}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-terracotta/20 bg-terracotta/5 p-5 text-[14px] text-charcoal/80">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />
          <p>
            Deletion is permanent. Some records may be retained in an anonymized form for
            legal, accounting, or fraud-prevention purposes as required by law. See our{" "}
            <Link to="/privacy" className="font-semibold text-terracotta hover:underline">
              Privacy Policy
            </Link>{" "}
            for details.
          </p>
        </div>
      </div>
    </PageShell>
  );
}
